import React from 'react';
import { Link } from 'react-router-dom';

const TermsOfService = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <header className="mb-10 pb-6 border-b-2 border-gray-200">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Terms of Service</h1>
          <p className="text-gray-600 italic">Effective Date: January 2025</p>
        </header>

        <main className="prose prose-lg max-w-none">
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
              <span className="w-1 h-6 bg-primary-600 mr-3 rounded"></span>
              1. Acceptance of Terms
            </h2>
            <p className="text-gray-700 leading-relaxed">
              By accessing or using GUARDLY's services ("Services"), you agree to be bound by these Terms of Service ("Terms"). 
              If you do not agree to these Terms, please do not use our Services.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
              <span className="w-1 h-6 bg-primary-600 mr-3 rounded"></span>
              2. Service Description
            </h2>
            <p className="text-gray-700 leading-relaxed">
              GUARDLY is a platform that connects customers with professional personal security services ("Security Service Providers"). 
              We facilitate the booking, management, and payment for security services but do not directly provide security services ourselves.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
              <span className="w-1 h-6 bg-primary-600 mr-3 rounded"></span>
              3. Eligibility
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">To use our Services, you must:</p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>Be at least 18 years old</li>
              <li>Have the legal capacity to enter into binding contracts</li>
              <li>Provide accurate and complete registration information</li>
              <li>Maintain the security of your account credentials</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
              <span className="w-1 h-6 bg-primary-600 mr-3 rounded"></span>
              4. User Accounts
            </h2>
            
            <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">4.1 Customer Accounts</h3>
            <p className="text-gray-700 leading-relaxed">
              Customers must provide valid contact information and payment details. You are responsible for all activities under your account.
            </p>
            
            <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">4.2 Security Service Provider Accounts</h3>
            <p className="text-gray-700 leading-relaxed mb-4">Security Service Providers must:</p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>Submit valid professional credentials and licenses</li>
              <li>Pass our background verification process</li>
              <li>Maintain current insurance coverage</li>
              <li>Comply with all applicable laws and regulations</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
              <span className="w-1 h-6 bg-primary-600 mr-3 rounded"></span>
              5. Booking and Services
            </h2>
            
            <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">5.1 Booking Process</h3>
            <p className="text-gray-700 leading-relaxed">
              Customers can request security services through our platform. Security Service Providers may accept or decline requests based on availability and suitability.
            </p>
            
            <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">5.2 Service Fees</h3>
            <p className="text-gray-700 leading-relaxed">
              Service fees are displayed before booking confirmation. GUARDLY charges a platform fee in addition to the Security Service Provider's rate.
            </p>
            
            <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">5.3 Cancellation and Refund Policy</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              Our cancellation policy is designed to be fair to both customers and Security Service Providers. Cancellation fees are based on 
              the time remaining before your scheduled session:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li><strong>Free Cancellation:</strong> If more than 48 hours before session AND cancelled within 24 hours of booking</li>
              <li><strong>10% Cancellation Fee:</strong> Cancellations made 12-48 hours before session (90% refund)</li>
              <li><strong>20% Cancellation Fee:</strong> Cancellations made 6-12 hours before session (80% refund)</li>
              <li><strong>30% Cancellation Fee:</strong> Cancellations made less than 6 hours before session (70% refund)</li>
              <li><strong>No Refund:</strong> After the session start time has passed</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-4">
              <strong>Important Notes:</strong>
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>All refunds are processed automatically upon cancellation</li>
              <li>Refunds typically appear in your account within 5-10 business days</li>
              <li>The 24-hour grace period for free cancellation only applies if your session is more than 48 hours away</li>
              <li>By booking a session, you agree to these cancellation terms</li>
              <li>In cases of documented emergencies, please contact support for special consideration</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
              <span className="w-1 h-6 bg-primary-600 mr-3 rounded"></span>
              6. Safety and Conduct
            </h2>
            
            <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">6.1 User Conduct</h3>
            <p className="text-gray-700 leading-relaxed mb-4">All users must:</p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>Treat others with respect and professionalism</li>
              <li>Not engage in harassment, discrimination, or violence</li>
              <li>Comply with all applicable laws</li>
              <li>Not misuse the platform or attempt to circumvent fees</li>
            </ul>
            
            <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">6.2 Verification and Screening</h3>
            <p className="text-gray-700 leading-relaxed">
              While we conduct background checks on Security Service Providers, users acknowledge that GUARDLY cannot guarantee the suitability or safety of any individual.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
              <span className="w-1 h-6 bg-primary-600 mr-3 rounded"></span>
              7. Payment Terms
            </h2>
            
            <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">7.1 Payment Processing</h3>
            <p className="text-gray-700 leading-relaxed">
              Payments are processed securely through our third-party payment providers. We do not store credit card information.
            </p>
            
            <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">7.2 Elite Membership</h3>
            <p className="text-gray-700 leading-relaxed">
              Elite membership fees are billed monthly and are non-refundable. Benefits and pricing are subject to change with 30 days notice.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
              <span className="w-1 h-6 bg-primary-600 mr-3 rounded"></span>
              8. Limitation of Liability
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">GUARDLY IS A PLATFORM PROVIDER AND IS NOT LIABLE FOR:</p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>Actions or omissions of Security Service Providers or Customers</li>
              <li>Quality of security services provided</li>
              <li>Indirect, incidental, or consequential damages</li>
              <li>Damages exceeding the fees paid in the past 12 months</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
              <span className="w-1 h-6 bg-primary-600 mr-3 rounded"></span>
              9. Indemnification
            </h2>
            <p className="text-gray-700 leading-relaxed">
              You agree to indemnify and hold GUARDLY harmless from any claims, damages, or expenses arising from your use of the Services or violation of these Terms.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
              <span className="w-1 h-6 bg-primary-600 mr-3 rounded"></span>
              10. Privacy
            </h2>
            <p className="text-gray-700 leading-relaxed">
              Your use of our Services is subject to our <Link to="/privacy" className="text-primary-600 hover:text-primary-700 font-medium">Privacy Policy</Link>, 
              which describes how we collect, use, and protect your information.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
              <span className="w-1 h-6 bg-primary-600 mr-3 rounded"></span>
              11. Intellectual Property
            </h2>
            <p className="text-gray-700 leading-relaxed">
              All content, trademarks, and intellectual property on our platform belong to GUARDLY or our licensors. 
              You may not use our intellectual property without written permission.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
              <span className="w-1 h-6 bg-primary-600 mr-3 rounded"></span>
              12. Dispute Resolution
            </h2>
            
            <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">12.1 Arbitration</h3>
            <p className="text-gray-700 leading-relaxed">
              Any disputes will be resolved through binding arbitration in accordance with the rules of the American Arbitration Association.
            </p>
            
            <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">12.2 Governing Law</h3>
            <p className="text-gray-700 leading-relaxed">
              These Terms are governed by the laws of the State of Delaware, without regard to conflict of law principles.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
              <span className="w-1 h-6 bg-primary-600 mr-3 rounded"></span>
              13. Modifications to Terms
            </h2>
            <p className="text-gray-700 leading-relaxed">
              We may modify these Terms at any time. Continued use of our Services after changes constitutes acceptance of the modified Terms.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
              <span className="w-1 h-6 bg-primary-600 mr-3 rounded"></span>
              14. Termination
            </h2>
            <p className="text-gray-700 leading-relaxed">
              We reserve the right to suspend or terminate accounts that violate these Terms or for any other reason at our discretion.
            </p>
          </section>

          <section className="mb-12 bg-red-50 border border-red-200 rounded-lg p-6">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
              <span className="w-1 h-6 bg-primary-600 mr-3 rounded"></span>
              15. Contact Information
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">For questions about these Terms, please contact us at:</p>
            <address className="not-italic">
              <p className="text-gray-700">
                <strong>Email:</strong> <a href="mailto:contact@guardly.app" className="text-primary-600 hover:text-primary-700">contact@guardly.app</a>
              </p>
              <p className="text-gray-700">
                <strong>Website:</strong> <a href="https://www.guardly.app" className="text-primary-600 hover:text-primary-700">www.guardly.app</a>
              </p>
            </address>
          </section>
        </main>
      </div>
    </div>
  );
};

export default TermsOfService;